if (!customElements.get('gift-box-option')) {
  const GIFT_BOX_VARIANT_ATTR = 'data-variant-id';

  class GiftBoxOption extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      if (!this.checkbox) return;

      this.variantId = Number(this.getAttribute(GIFT_BOX_VARIANT_ATTR));
      this.checkbox.addEventListener('change', () => this.onToggle());

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.origin === this) return;
          this.syncFromCart(event);
        });
      }

      this.syncFromCart();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    get lineKey() {
      return this.dataset.lineKey || '';
    }

    setBusy(busy) {
      this.checkbox.disabled = busy;
      this.classList.toggle('gift-box-option--busy', busy);
      this.querySelectorAll('[name="checkout"]').forEach((button) => {
        button.toggleAttribute('disabled', busy);
      });
      const checkout = this.closest('cart-notification, .drawer__footer, #main-cart-footer')?.querySelector(
        '[name="checkout"]'
      );
      if (checkout) checkout.toggleAttribute('disabled', busy);
    }

    async fetchCart() {
      const response = await fetch(`${routes.cart_url}.js`);
      return response.json();
    }

    findLine(cart) {
      return (cart.items || []).find((item) => Number(item.variant_id) === this.variantId);
    }

    hasGiftableItems(cart) {
      const excludedHandles = new Set(['salad-spoons', 'palace-arch-cheese-board']);
      return (cart.items || []).some((item) => {
        if (Number(item.variant_id) === this.variantId) return false;
        if (item.gift_card) return false;
        if (excludedHandles.has(item.handle)) return false;
        return item.requires_shipping !== false;
      });
    }

    applyVisibility(cart, line) {
      const giftable = this.hasGiftableItems(cart);
      if (this.dataset.mode === 'product') {
        this.hidden = false;
        return giftable;
      }
      this.hidden = !giftable && !line;
      return giftable;
    }

    async syncFromCart(event) {
      try {
        const cart = await this.fetchCart();
        const line = this.findLine(cart);
        const giftable = this.applyVisibility(cart, line);

        // Skip while we add the box first on the product form, or the box
        // would be removed before the product lands in the cart.
        const skipAutoRemove = event?.source === 'gift-box' || event?.source === 'product-form';
        if (line && !giftable && this.dataset.mode !== 'product' && !skipAutoRemove) {
          await this.removeFromCart(line.key);
          return;
        }

        this.dataset.inCart = line ? 'true' : 'false';
        this.dataset.lineKey = line?.key || '';
        if (line && Number(line.quantity) > 1) {
          await this.changeQuantity(line.key, 1);
          return;
        }
        this.checkbox.checked = Boolean(line);
      } catch (error) {
        console.error(error);
      }
    }

    async onToggle() {
      if (this.dataset.mode === 'product' && this.dataset.inCart !== 'true' && !this.checkbox.checked) {
        return;
      }

      if (this.dataset.mode === 'product' && this.checkbox.checked && this.dataset.inCart !== 'true') {
        try {
          const cart = await this.fetchCart();
          if (!this.hasGiftableItems(cart)) return;
        } catch (error) {
          console.error(error);
          return;
        }
      }

      this.setBusy(true);
      try {
        if (this.checkbox.checked) {
          await this.addToCart();
        } else {
          await this.removeFromCart(this.lineKey);
        }
      } catch (error) {
        console.error(error);
        this.checkbox.checked = !this.checkbox.checked;
      } finally {
        this.setBusy(false);
      }
    }

    async ensureInCart() {
      if (!this.checkbox?.checked) return;
      const cart = await this.fetchCart();
      if (this.findLine(cart)) {
        this.dataset.inCart = 'true';
        return;
      }
      await this.addToCart({ skipPublish: false });
    }

    sectionIds() {
      const ids = ['cart-icon-bubble'];
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      if (items?.dataset.id) ids.push(items.dataset.id);
      if (footer?.dataset.id) ids.push(footer.dataset.id);
      return ids;
    }

    applySections(data) {
      if (!data?.sections) return;

      if (data.sections['cart-icon-bubble']) {
        const dest = document.getElementById('cart-icon-bubble');
        const parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const src = parsed.querySelector('.shopify-section') || parsed.body;
        if (dest && src) dest.innerHTML = src.innerHTML;
      }

      const itemsWrap = document.getElementById('main-cart-items');
      if (itemsWrap?.dataset.id && data.sections[itemsWrap.dataset.id]) {
        const parsed = new DOMParser().parseFromString(data.sections[itemsWrap.dataset.id], 'text/html');
        const src = parsed.querySelector('.js-contents');
        const dest = itemsWrap.querySelector('.js-contents');
        if (src && dest) dest.innerHTML = src.innerHTML;
      }

      const footer = document.getElementById('main-cart-footer');
      if (footer?.dataset.id && data.sections[footer.dataset.id]) {
        const parsed = new DOMParser().parseFromString(data.sections[footer.dataset.id], 'text/html');
        const src = parsed.querySelector('.js-contents');
        const dest = footer.querySelector('.js-contents');
        if (src && dest) dest.innerHTML = src.innerHTML;
      }

      if (typeof data.item_count === 'number') {
        document.querySelector('cart-items')?.classList.toggle('is-empty', data.item_count === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', data.item_count === 0);
      }
    }

    async addToCart() {
      const cart = await this.fetchCart();
      const existing = this.findLine(cart);
      if (existing) {
        this.dataset.inCart = 'true';
        this.dataset.lineKey = existing.key;
        this.checkbox.checked = true;
        if (Number(existing.quantity) > 1) {
          await this.changeQuantity(existing.key, 1);
        }
        return;
      }
      const config = fetchConfig('javascript');
      config.body = JSON.stringify({
        id: this.variantId,
        quantity: 1,
        sections: this.sectionIds(),
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Unable to add gift box');
      this.applySections(data);
      this.dataset.inCart = 'true';
      if (data.key) this.dataset.lineKey = data.key;
      await this.publishUpdate();
    }

    async removeFromCart(lineKey) {
      let key = lineKey;
      if (!key) {
        const cart = await this.fetchCart();
        key = this.findLine(cart)?.key;
      }
      if (!key) {
        this.dataset.inCart = 'false';
        this.dataset.lineKey = '';
        this.checkbox.checked = false;
        return;
      }
      await this.changeQuantity(key, 0);
    }

    async changeQuantity(key, quantity) {
      const config = fetchConfig('javascript');
      config.body = JSON.stringify({
        id: key,
        quantity,
        sections: this.sectionIds(),
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${routes.cart_change_url}`, config);
      const data = await response.json();
      if (data.errors) throw new Error(data.errors);
      this.applySections(data);
      this.dataset.inCart = quantity > 0 ? 'true' : 'false';
      this.dataset.lineKey = quantity > 0 ? key : '';
      this.checkbox.checked = quantity > 0;
      await this.publishUpdate();
    }

    async publishUpdate() {
      if (typeof publish !== 'function' || !window.PUB_SUB_EVENTS?.cartUpdate) return;
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-box', origin: this });
    }
  }

  customElements.define('gift-box-option', GiftBoxOption);
}
